import React, { useState, useEffect } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { StatusPill } from "../components/StatusPill";
import { useApp } from "../context/AppContext";
import { CalendarDays, Plus, X, Clock, CheckCircle2, AlertCircle } from "lucide-react";

const LEAVE_TYPES = [
  { id: "CL", label: "Casual Leave (CL)" },
  { id: "ML", label: "Comp Off Leave" },
  { id: "LOP", label: "Loss of Pay (LOP)" },
];

function todayIst() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function countDays(from, to) {
  if (!from || !to) return 0;
  const f = new Date(from), t = new Date(to);
  return Math.max(0, Math.round((t - f) / 86400000) + 1);
}

function typeLabel(type) {
  const t = String(type || "CL").toUpperCase();
  if (t === "ML") return "Comp Off";
  if (t === "LOP") return "LOP";
  return "CL";
}

export function LeaveScreen({ emp }) {
  const { applyLeave, fetchLeaves } = useApp();
  const [balance, setBalance] = useState(emp.leaveBalance || {
    total: 12, used: 0, remaining: 12,
    clTotal: 6, clUsed: 0, clRemaining: 6,
    mlTotal: 6, mlUsed: 0, mlRemaining: 6
  });
  const [applications, setApplications] = useState(emp.leaveApplications || []);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lopSubmitting, setLopSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    leaveType: "CL",
    from: todayIst(),
    to: todayIst(),
    reason: "",
  });

  useEffect(() => {
    if (fetchLeaves) {
      fetchLeaves().then(data => {
        if (data?.balance) setBalance(data.balance);
        if (data?.data) setApplications(data.data);
      }).catch(() => {});
    }
  }, []);

  const days = countDays(form.from, form.to);
  const clRemaining = balance.clRemaining ?? Math.max(0, (balance.clTotal ?? 6) - (balance.clUsed || 0));
  const mlRemaining = balance.mlRemaining ?? Math.max(0, (balance.mlTotal ?? 6) - (balance.mlUsed || 0));
  const willBeLop = form.leaveType === "LOP"
    || (form.leaveType === "CL" && days > clRemaining)
    || (form.leaveType === "ML" && days > mlRemaining);
  const today = todayIst();
  const alreadyCheckedIn = !!(emp.checkedIn || emp.checked_in || emp.checkInTime || emp.check_in_time);
  const coveringToday = applications.some(a => {
    const st = String(a.status || "").toLowerCase();
    if (st !== "approved" && st !== "pending") return false;
    const from = a.from || a.from_date;
    const to = a.to || a.to_date;
    return from && to && from <= today && today <= to;
  });
  const canMarkLopToday = !alreadyCheckedIn && !coveringToday;
  const lopDaysThisYear = applications
    .filter(a => String(a.leave_type || a.leaveType || "").toUpperCase() === "LOP" && String(a.status || "").toLowerCase() === "approved")
    .reduce((n, a) => n + countDays(a.from || a.from_date, a.to || a.to_date), 0);

  const refresh = async () => {
    if (!fetchLeaves) return;
    const data = await fetchLeaves();
    if (data?.balance) setBalance(data.balance);
    if (data?.data) setApplications(data.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    if (days <= 0) { setError("Invalid date range."); return; }
    if (!form.reason.trim() && form.leaveType !== "LOP") { setError("Please provide a reason."); return; }
    if (willBeLop && !window.confirm(
      clRemaining <= 0 || form.leaveType === "LOP"
        ? `Record ${days} day${days > 1 ? "s" : ""} as unpaid LOP (Loss of Pay)? This is recorded immediately.`
        : `Only ${form.leaveType === "ML" ? mlRemaining : clRemaining} paid leave day(s) remaining. Extra days will be unpaid LOP. Continue?`
    )) return;
    setSubmitting(true);
    try {
      const res = await applyLeave(form);
      const unpaid = res?.data?.unpaid || res?.data?.data?.unpaid || willBeLop;
      setSuccess(unpaid
        ? "Recorded as unpaid LOP (Loss of Pay). No manager approval required."
        : "Leave application submitted. Pending manager approval.");
      setShowForm(false);
      setForm({ leaveType: "CL", from: todayIst(), to: todayIst(), reason: "" });
      await refresh();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit leave.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkLop = async () => {
    setError(""); setSuccess("");
    if (!canMarkLopToday) {
      setError(alreadyCheckedIn
        ? "Cannot mark LOP — you have already checked in today."
        : "Leave or LOP is already recorded for today.");
      return;
    }
    if (!window.confirm(`Mark today (${today}) as Loss of Pay (LOP)? This is unpaid and recorded immediately.`)) return;
    setLopSubmitting(true);
    try {
      await applyLeave({ leaveType: "LOP", from: today, to: today, reason: "Marked as LOP" });
      setSuccess("Today marked as unpaid LOP. No manager approval required.");
      await refresh();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to mark LOP.");
    } finally {
      setLopSubmitting(false);
    }
  };

  return (
    <div>
      <Card style={{ background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`, color: "#fff", padding: "14px 16px", marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#9FB0C9", letterSpacing: 0.5 }}>ANNUAL LEAVE ALLOWANCE</div>
        <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.gold, marginTop: 2 }}>
          12 Days / Year
        </div>
        <div style={{ fontSize: 11.5, color: "#9FB0C9", marginTop: 3 }}>
          6 Casual Leaves (CL) + 6 Comp Off Leaves. You can still apply when CL is 0 — it is recorded as unpaid LOP.
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
        <Card style={{ padding: 12, textAlign: "center", border: clRemaining <= 0 ? `1.5px solid ${TOKENS.danger}55` : undefined }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: clRemaining <= 0 ? TOKENS.danger : TOKENS.navyDeep }}>
            {clRemaining}/6
          </div>
          <div style={{ fontSize: 9.5, color: TOKENS.muted, marginTop: 2, fontWeight: 700 }}>CL REMAINING</div>
        </Card>
        <Card style={{ padding: 12, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.navyDeep }}>
            {mlRemaining}/6
          </div>
          <div style={{ fontSize: 9.5, color: TOKENS.muted, marginTop: 2, fontWeight: 700 }}>COMP OFF REMAINING</div>
        </Card>
        <Card style={{ padding: 12, textAlign: "center" }}>
          <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: TOKENS.warning }}>
            {lopDaysThisYear}
          </div>
          <div style={{ fontSize: 9.5, color: TOKENS.muted, marginTop: 2, fontWeight: 700 }}>LOP DAYS</div>
        </Card>
      </div>
      {clRemaining <= 0 && (
        <div style={{ fontSize: 12, color: TOKENS.warning, fontWeight: 700, marginBottom: 12 }}>
          No Casual Leave remaining. Apply leave anyway — it will be unpaid LOP.
        </div>
      )}

      {success && (
        <div style={{ background: TOKENS.successBg, border: `1px solid ${TOKENS.success}`, borderRadius: 10, padding: "10px 14px", fontSize: 12.5, color: TOKENS.success, fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle2 size={15} /> {success}
        </div>
      )}
      {error && (
        <div style={{ background: TOKENS.dangerBg, border: `1px solid ${TOKENS.danger}`, borderRadius: 10, padding: "10px 14px", fontSize: 12.5, color: TOKENS.danger, fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <AlertCircle size={15} /> {error}
        </div>
      )}

      {!showForm && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          <button onClick={() => setShowForm(true)} style={{
            width: "100%", background: TOKENS.navyDeep, color: "#fff",
            border: "none", borderRadius: 13, padding: "13px 16px",
            fontWeight: 700, fontSize: 13.5, display: "flex", alignItems: "center",
            justifyContent: "center", gap: 8, cursor: "pointer",
          }}>
            <Plus size={17} /> Apply for Leave
          </button>
        </div>
      )}

      {showForm && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 15, fontWeight: 600, color: TOKENS.navyDeep }}>
              Leave Application
            </div>
            <button onClick={() => { setShowForm(false); setError(""); }} style={{ border: "none", background: "none", cursor: "pointer" }}>
              <X size={18} color={TOKENS.muted} />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>LEAVE TYPE</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              {LEAVE_TYPES.map(lt => (
                <button key={lt.id} type="button" onClick={() => setForm(f => ({ ...f, leaveType: lt.id }))} style={{
                  flex: 1, minWidth: 90, padding: "8px 10px", borderRadius: 10, border: `1.5px solid ${form.leaveType === lt.id ? TOKENS.navyDeep : TOKENS.border}`,
                  background: form.leaveType === lt.id ? TOKENS.navyDeep : "#fff",
                  color: form.leaveType === lt.id ? "#fff" : TOKENS.ink,
                  fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                }}>{lt.label}</button>
              ))}
            </div>

            <div style={{ background: TOKENS.cream, border: `1px solid ${TOKENS.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 12 }}>
              Remaining CL: {clRemaining} · Remaining Comp Off: {mlRemaining}
            </div>

            {willBeLop && (
              <div style={{ background: TOKENS.warningBg, border: `1px solid ${TOKENS.warning}55`, borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, color: TOKENS.warning, marginBottom: 12 }}>
                {form.leaveType === "LOP"
                  ? "This will be recorded immediately as unpaid LOP (no CL deducted)."
                  : "No paid leave remaining for this request. It will be recorded as unpaid LOP instead of being blocked."}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              {[["From Date", "from"], ["To Date", "to"]].map(([label, field]) => (
                <div key={field}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 5 }}>{label.toUpperCase()}</label>
                  <input type="date" value={form[field]}
                    onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    style={{ width: "100%", padding: "9px 11px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none", background: TOKENS.cream }}
                  />
                </div>
              ))}
            </div>

            {days > 0 && (
              <div style={{ background: `${TOKENS.gold}18`, border: `1px solid ${TOKENS.gold}44`, borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <Clock size={13} /> {days} day{days > 1 ? "s" : ""} selected
              </div>
            )}

            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>REASON {form.leaveType === "LOP" ? "(OPTIONAL)" : ""}</label>
            <textarea
              placeholder={form.leaveType === "LOP" ? "Optional note for LOP…" : "State your reason for leave…"}
              value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              rows={3}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid ${TOKENS.border}`, fontSize: 13, color: TOKENS.ink, outline: "none", background: TOKENS.cream, resize: "none", marginBottom: 14 }}
            />

            <button type="submit" disabled={submitting} style={{
              width: "100%", background: TOKENS.navyDeep, color: "#fff",
              border: "none", borderRadius: 12, padding: "12px", fontWeight: 700,
              fontSize: 13, cursor: submitting ? "wait" : "pointer", opacity: submitting ? 0.7 : 1,
            }}>
              {submitting ? "Submitting…" : willBeLop ? "Submit as Unpaid LOP" : "Submit Leave Application"}
            </button>
          </form>
        </Card>
      )}

      <SectionLabel>My Leave Applications</SectionLabel>
      {applications.length === 0 && (
        <Card style={{ textAlign: "center", color: TOKENS.muted, fontSize: 13, padding: 24 }}>
          <CalendarDays size={28} color={TOKENS.border} style={{ margin: "0 auto 8px" }} />
          No leave applications yet.
        </Card>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {applications.map(a => {
          const nDays = countDays(a.from, a.to);
          const kind = typeLabel(a.leave_type || a.leaveType);
          return (
            <Card key={a.id} style={{ padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{
                      background: kind === "LOP" ? TOKENS.warningBg : `${TOKENS.navyDeep}12`,
                      color: kind === "LOP" ? TOKENS.warning : TOKENS.navyDeep,
                      borderRadius: 8, padding: "2px 8px", fontSize: 11, fontWeight: 700
                    }}>
                      {kind}
                    </span>
                    <span style={{ fontSize: 12, color: TOKENS.muted }}>{nDays} day{nDays > 1 ? "s" : ""}</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.ink }}>
                    {a.from} → {a.to}
                  </div>
                  <div style={{ fontSize: 12, color: TOKENS.muted, marginTop: 3 }}>{a.reason}</div>
                  {a.rejectReason && (
                    <div style={{ fontSize: 11, color: TOKENS.danger, marginTop: 4, fontWeight: 600 }}>
                      Rejection reason: {a.rejectReason}
                    </div>
                  )}
                </div>
                <StatusPill status={a.status} />
              </div>
            </Card>
          );
        })}
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}
